// «خدمت» on the «بیمار بعدی» hero — what the next patient is coming FOR.
//
// The appointment row cannot answer this. `Appointment.type` is online |
// in_person and scheduling's own visit_type is a dead column the service never
// populates: the clinic desk books an hour with a dentist, not a treatment. The
// answer lives in the patient's طرح درمان — whatever a previous visit agreed
// and did not carry out is what the next sitting is for, and a patient with no
// outstanding line is coming for a ویزیت.
//
// ── One patient, not the day sheet ─────────────────────────────────────────
// This reads the plan of exactly ONE patient: the one `buildDayModel()` called
// next. Hanging the same read off every row would be a plan read and a record
// read per appointment on every paint of a twenty-row day, for a fact the
// screen shows in one place.
//
// ── And it does not ride the day poll ──────────────────────────────────────
// useToday re-reads every 30s so the desk's «پذیرش شد» lands. A plan does not
// change on that clock — it changes when this dentist edits it, inside the
// visit, on another screen — so this fires on the PATIENT changing and not on
// the tick. The cost of being a minute stale here is a name; the cost of
// polling two more reads forever is every dentist's battery.

import React from 'react';
import { bffQuery } from '../lib/bffClient.js';
import {
  TREATMENT_PLANS_QUERY, TREATMENT_RECORDS_QUERY, nextPlannedService,
} from '../screens/Visit/Visit.shared.js';

/**
 * The first outstanding plan line for this patient, or null.
 *
 * Null is «nothing is owed» AND «we could not tell», deliberately: both render
 * as «ویزیت», which is what the appointment is until something says otherwise.
 * Naming a treatment off a failed read would be worse than naming none.
 */
export function useNextService({ patientId, clinicId }) {
  const [service, setService] = React.useState(null);

  React.useEffect(() => {
    // A «ثبت سریع» walk-in has no patient record, so there is no plan to read.
    if (!patientId) {
      setService(null);
      return undefined;
    }

    let alive = true;
    // Cleared while the next patient's plan is read: leaving the previous
    // patient's treatment under a new patient's name is the one wrong answer
    // this hook can give.
    setService(null);

    (async () => {
      const vars = { patientId, clinicId: clinicId || null };
      const [plans, records] = await Promise.allSettled([
        bffQuery(TREATMENT_PLANS_QUERY, vars),
        bffQuery(TREATMENT_RECORDS_QUERY, vars),
      ]);
      if (!alive) return;

      if (plans.status !== 'fulfilled') {
        console.error('[useNextService] plan read failed:', plans.reason?.message);
        return;
      }
      // A failed RECORD read is not a failure of the whole answer: `sessionsOf`
      // falls back to the line's own «انجام‌شده» status, which is what every
      // plan written before records existed carries anyway.
      if (records.status !== 'fulfilled') {
        console.error('[useNextService] record read failed:', records.reason?.message);
      }

      setService(nextPlannedService(
        plans.value?.treatmentPlans || [],
        records.status === 'fulfilled' ? records.value?.treatmentRecords || [] : [],
      ));
    })();

    return () => { alive = false; };
  }, [patientId, clinicId]);

  return service;
}

export default useNextService;
